/**
 * VerdictBadge — traffic-light visual for laydown quality (Phase BM2).
 *
 * Maps the algorithm's `within_band` classification (BEST → ALARMING_LIMIT,
 * REJECT) into a 3-state verdict: GOOD / WARNING / BAD. Two sizes:
 *
 *   <VerdictBadge band="COMFORTABLE" size="full"  baleCount={60} />
 *   <VerdictBadge band="POOR"        size="pill"  />          // inline chip
 *   <VerdictBadge band="ALARMING_LIMIT" size="compact" />     // small card
 *
 * Used by:
 *   - QuickLaydownPage (operator-mode result screen)
 *   - LaydownPlanningPage (QC suggest drawer result)
 *   - QualityPostureDashboard (Phase 2, bullseye dots)
 *
 * Spec: Uster slide 22 + blueprint Sec 22-29.
 */
import { Card, Tag, Typography } from 'antd'
import {
  CheckCircleFilled, WarningFilled, CloseCircleFilled,
} from '@ant-design/icons'

const { Text } = Typography

// ── The 7 Uster bands → 3 traffic-light verdicts ─────────────────────────────
export type WithinBand =
  | 'BEST' | 'BETTER' | 'COMFORTABLE'         // GOOD
  | 'POOR' | 'NON_COMFORTABLE'                // WARNING
  | 'ALARMING_LIMIT' | 'REJECT'               // BAD
  | string | null | undefined                 // safety: unknown → BAD

export type Verdict = 'GOOD' | 'WARNING' | 'BAD'

export function bandToVerdict(band: WithinBand): Verdict {
  if (!band) return 'BAD'
  if (band === 'BEST' || band === 'BETTER' || band === 'COMFORTABLE') return 'GOOD'
  if (band === 'POOR' || band === 'NON_COMFORTABLE') return 'WARNING'
  return 'BAD'
}

// ── Visual definition per verdict ────────────────────────────────────────────
type VerdictMeta = {
  color: string
  bg: string
  icon: React.ReactNode
  label: string
  subtext: string
}

const VERDICT_META: Record<Verdict, VerdictMeta> = {
  GOOD: {
    color: '#52c41a',
    bg: '#f6ffed',
    icon: <CheckCircleFilled />,
    label: 'GOOD',
    subtext: 'Within tier limits',
  },
  WARNING: {
    color: '#faad14',
    bg: '#fffbe6',
    icon: <WarningFilled />,
    label: 'WARNING',
    subtext: 'Some metrics over limit',
  },
  BAD: {
    color: '#ff4d4f',
    bg: '#fff2f0',
    icon: <CloseCircleFilled />,
    label: 'NOT OK',
    subtext: 'Outside acceptable range',
  },
}

// AntD Tag color names matching the verdict
const VERDICT_TAG_COLOR: Record<Verdict, string> = {
  GOOD: 'green',
  WARNING: 'orange',
  BAD: 'red',
}

// ── Component ────────────────────────────────────────────────────────────────
type Props = {
  band: WithinBand
  /** Visual layout. Default: 'full'. */
  size?: 'full' | 'compact' | 'pill'
  /** Bale count for the result subtitle (only used at size='full'). */
  baleCount?: number
  /** Override label (defaults to GOOD/WARNING/NOT OK). */
  label?: string
  /** Override sub-text (defaults to "Within tier limits" etc.). */
  subtext?: string
}

export default function VerdictBadge({
  band, size = 'full', baleCount, label, subtext,
}: Props) {
  const verdict = bandToVerdict(band)
  const m = VERDICT_META[verdict]
  const finalLabel = label ?? m.label
  const finalSubtext = subtext ?? m.subtext

  if (size === 'pill') {
    return (
      <Tag color={VERDICT_TAG_COLOR[verdict]} style={{ fontSize: 12, padding: '2px 10px' }}>
        {finalLabel}
      </Tag>
    )
  }

  if (size === 'compact') {
    return (
      <Card
        size="small"
        style={{
          background: m.bg, borderColor: m.color, borderWidth: 1,
          textAlign: 'center', padding: 0,
        }}
        styles={{ body: { padding: 12 } }}
      >
        <div style={{ fontSize: 28, color: m.color, lineHeight: 1 }}>{m.icon}</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: m.color, marginTop: 4 }}>
          {finalLabel}
        </div>
        <Text type="secondary" style={{ fontSize: 11 }}>{band ?? 'unknown'}</Text>
      </Card>
    )
  }

  // size = 'full' — operator-friendly tablet card
  return (
    <Card
      style={{
        background: m.bg, borderColor: m.color, borderWidth: 2,
        borderRadius: 16, marginBottom: 16,
      }}
      styles={{ body: { padding: 24, textAlign: 'center' } }}
    >
      <div style={{ fontSize: 80, color: m.color, lineHeight: 1 }}>{m.icon}</div>
      <div style={{ fontSize: 32, fontWeight: 700, color: m.color, marginTop: 8 }}>
        {finalLabel}
      </div>
      <Text type="secondary" style={{ fontSize: 16 }}>{finalSubtext}</Text>
      {baleCount != null && (
        <div style={{ marginTop: 12, fontSize: 22, fontWeight: 600 }}>
          {baleCount} bales picked
        </div>
      )}
    </Card>
  )
}
